/**
 * Pure layout math for the cockpit: how the original's **logical 320x200 VGA
 * frame** (AD-009) maps onto the browser canvas, and which instrument slots
 * are drawn into it.
 *
 * The cockpit is composed at 1:1 into a 320x200 offscreen canvas from the
 * baked per-aircraft composition (`cockpit-composition.ts`), exactly as
 * `RB.EXE` composes it, and only then presented — **integer** nearest-
 * neighbour upscale, VGA 1:1.2 pixel-aspect correction, anchored
 * bottom-centre. Nothing is ever stretched to fit the window: the previous
 * cockpit did that to a single 192-px dash fragment, which is what the user
 * saw as "only renders 50%... cuts off on the side".
 *
 * No canvas/image dependency here, so the whole mapping is testable without
 * a DOM; `panel-render.ts` is the untested (canvas-mutating) half, the same
 * split `assets/waveform.ts`'s `computePeaks`/`drawPeaks` already uses.
 */

import type { CockpitGaugeRole, CockpitGaugeSlot } from "@red-opus/baker/schema";

/** The original's frame buffer. Every composition coordinate is in this space. */
export const LOGICAL_WIDTH = 320;
export const LOGICAL_HEIGHT = 200;

/**
 * VGA mode 13h's pixel aspect: 320x200 filled a 4:3 display, so each pixel
 * is 1.2x taller than it is wide. Presenting square pixels would make the
 * dials visibly oval.
 */
export const VGA_PIXEL_ASPECT = 1.2;

export interface Rect {
  readonly x: number;
  readonly y: number;
  readonly width: number;
  readonly height: number;
}

/**
 * Where the 320x200 frame lands on a `canvasWidth` x `canvasHeight` surface.
 * `scaleX`/`scaleY` are both whole numbers of device pixels per source pixel
 * — one source pixel becomes a crisp `scaleX` x `scaleY` block, so nearest-
 * neighbour stays exact in *both* axes. `scaleY` is `scaleX` x 1.2 rounded,
 * which is exact whenever `scaleX` is a multiple of 5 and within ~10%
 * otherwise; a non-integer vertical scale would resample rows and undo the
 * point of composing at 320x200 in the first place.
 */
export interface PresentTransform {
  readonly scaleX: number;
  readonly scaleY: number;
  /** Destination rect of the whole logical frame, anchored bottom-centre. */
  readonly rect: Rect;
}

/** Largest `scaleX` whose 320x(200 x 1.2 x scaleX) block still fits; always at least 1, so a tiny window shows a clipped panel rather than nothing. */
export function presentTransform(canvasWidth: number, canvasHeight: number): PresentTransform {
  let scaleX = 1;
  while (true) {
    const next = scaleX + 1;
    const nextScaleY = Math.max(1, Math.round(next * VGA_PIXEL_ASPECT));
    if (LOGICAL_WIDTH * next > canvasWidth || LOGICAL_HEIGHT * nextScaleY > canvasHeight) break;
    scaleX = next;
  }
  const scaleY = Math.max(1, Math.round(scaleX * VGA_PIXEL_ASPECT));
  const width = LOGICAL_WIDTH * scaleX;
  const height = LOGICAL_HEIGHT * scaleY;
  return {
    scaleX,
    scaleY,
    rect: { x: Math.round((canvasWidth - width) / 2), y: canvasHeight - height, width, height },
  };
}

/**
 * The WWI-authentic "big five" the manual's REALISTIC INSTRUMENTS wording
 * implies ("the only instruments seen from the cockpit will be the ones that
 * existed on the original WWI aircraft", `docs/game/manual/04-gameplay.md:77`)
 * — airspeed, altimeter, compass, tachometer, oil. The fuel sight-tube and
 * the unlabelled leftmost dial are the "all possible instruments" extras.
 *
 * The live capture shows all seven at mission start with no toggle touched,
 * which independently confirms the toggle defaults **off**.
 */
export const ERA_AUTHENTIC_ROLES: ReadonlySet<CockpitGaugeRole> = new Set([
  "airspeed",
  "altimeter",
  "compass",
  "tachometer",
  "oil",
]);

/** The baked slot list, filtered to the era-authentic set when REALISTIC INSTRUMENTS is on. Order preserved. */
export function visibleGauges(
  gauges: readonly CockpitGaugeSlot[],
  realisticInstruments: boolean,
): readonly CockpitGaugeSlot[] {
  return realisticInstruments ? gauges.filter((slot) => ERA_AUTHENTIC_ROLES.has(slot.role)) : gauges;
}

/**
 * The compass card's viewing window inside its bezel, as an inset of the
 * bezel's own rect. MEASURED off the live capture: template-matching
 * `GAUGES.BMP` frame 6 (the 184x8 lettered tape) inside the Camel's compass
 * bezel puts the visible band at logical (279,171), i.e. 10 px in and 17 px
 * down from the bezel's (269,154) top-left, ~28 px of tape visible at once.
 */
export const COMPASS_WINDOW_INSET = { x: 10, y: 17, width: 28, height: 8 } as const;

export function compassWindowRect(bezel: CockpitGaugeSlot): Rect {
  return {
    x: bezel.x + COMPASS_WINDOW_INSET.x,
    y: bezel.y + COMPASS_WINDOW_INSET.y,
    width: COMPASS_WINDOW_INSET.width,
    height: COMPASS_WINDOW_INSET.height,
  };
}

/** Logical-frame point -> device-pixel point on the presented canvas. */
export function logicalToCanvas(transform: PresentTransform, x: number, y: number): { x: number; y: number } {
  return { x: transform.rect.x + x * transform.scaleX, y: transform.rect.y + y * transform.scaleY };
}
